import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {ComponentOption} from "#template/nodes/geometry-graph/options"
import {componentOption} from "#template/nodes/geometry-graph/options"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type GetAttributeComponentParameters<TAttribute> = {
    name: string
    attribute: TAttribute | null
    component: ComponentOption
}

export const getAttributeComponentParametersSchema = <TAttribute>(attributeSchema: z.ZodMiniType<TAttribute, TAttribute>) =>
    z.object({
        name: z.string(),
        attribute: z.nullable(attributeSchema),
        component: componentOption,
    })

function getAttributeComponent<T extends IndexingDim>(attribute: GeometryAttributeData<T>, component: ComponentOption): GeometryAttributeData<T> {
    const attrRefData = attribute.ref

    const getAttrRefData = applyGeometryOperator((attrRef: AttributeRef) => {
        switch (component) {
            case "x":
                return attrRef.x
            case "y":
                return attrRef.y
            case "z":
                return attrRef.z
            case "xy":
                return attrRef.xy
            case "xz":
                return attrRef.xz
            case "yx":
                return attrRef.yx
            case "yz":
                return attrRef.yz
            case "zx":
                return attrRef.zx
            case "zy":
                return attrRef.zy
        }
    }, attrRefData)

    return createGeometryAttributeData(getAttrRefData, attribute.dim)
}

export abstract class GeometryGetAttributeComponentNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, GetAttributeComponentParameters<TAttribute>> {
    protected override hasValidParameters(
        parameters: GetAttributeComponentParameters<TAttribute>,
    ): parameters is GetAttributeComponentParameters<TAttribute> & NonNullableParameters<GetAttributeComponentParameters<TAttribute>> {
        return parameters.attribute !== null && parameters.component !== undefined
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<GetAttributeComponentParameters<TAttribute>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attribute, component} = parameters

        const [evaluatedInputValid, evaluatedInputInvalid] = scope.branch(evaluator.evaluate(attribute))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedInputValid, component),
            ([evaluatedInputValid, mode]) => getAttributeComponent(evaluatedInputValid, mode),
            "getAttributeComponent",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: evaluatedInputInvalid}
    }
}
